// Interaction State Context
//
// Phase 3 of the UI-framework mega-arc split this OUT of UIContext. UIContext
// now owns ONLY window-visibility flags (window management is migrating to the
// unified engine); the NON-window interaction state lives here:
//   - NPC interaction (the live npc:interact → talkToNPC → npcDialogue
//     round-trip), shop UI, quest-offer UI
//   - chat-focus (suppresses hotkeys)
//   - tutorial active flag
//
// The data flow is preserved BYTE-FOR-BYTE from UIContext: the same EventBus
// subscriptions (npc:interact opens the panel + emits talkToNPC; the server
// net:message:npcDialogue reply hydrates the real quests/shop). GameUIOverlay
// reads this context exactly as it read UIContext before; hydrateNpcDialogue is
// unchanged.

import React, { createContext, ReactNode, useCallback, useContext, useState, useEffect, useRef } from 'react';
import { gameLogger } from '../../../utils/GameLogger';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { NPCData, getNPC } from '../../../shared/NPCData';
import { ShopItem } from '../types/ShopItem';
import type { NpcDialoguePayload } from '../npc/hydrateNpcDialogue';
import { mayHaveFeaturedTeaser } from '../npc/npcQuestEntries';
import { getQuest } from '../../../shared/content/generated/quests.gen';
import type { LearnAbilityResultMessage } from '../../network/ClientMessageTypes';
import { quoteShopPurchase } from '../npc/shop/purchaseQuote';
import { setActiveDialogueNpcId } from '../../systems/npc/activeDialogueNpc';

type CorrelatedNpcDialoguePayload = NpcDialoguePayload & {
  requestId?: number;
};

export type ShopState =
  | {
      isOpen: false;
      npcId: null;
      npcName: '';
      items: ShopItem[];
      questCount?: number;
    }
  | {
      isOpen: true;
      npcId: string;
      npcName: string;
      items: ShopItem[];
      questCount?: number;
    };

export interface ShopPurchasePayload {
  npcId: string;
  itemId: string;
  quantity: number;
}

const CLOSED_SHOP_STATE: ShopState = {
  isOpen: false,
  npcId: null,
  npcName: '',
  items: [],
};

export function buildShopPurchasePayload(
  shopState: ShopState,
  itemId: string,
  quantity = 1,
  playerGold?: number,
): ShopPurchasePayload | null {
  if (!shopState.isOpen) return null;
  const item = shopState.items.find(candidate => candidate.id === itemId);
  if (!item || !quoteShopPurchase(item, quantity, playerGold).canPurchase) return null;
  return { npcId: shopState.npcId, itemId, quantity };
}

interface QuestOfferState {
  isOpen: boolean;
}

/**
 * The quest just turned in at the open NPC (plan 13 §10). Shown at the top of
 * the refreshed quest page, or in place of the greeting when nothing is left
 * to offer, until the NPC surface closes.
 */
export interface QuestCompletionNotice {
  npcId: string;
  questId: string;
  /** The authored completion line (may carry NPC text markup). */
  message: string;
  /** The viewer's class, for the ability the quest taught (filled by the overlay). */
  playerClass?: string;
  /** Whether the rewarded ability was already known before turn-in (QER-06). */
  alreadyKnown?: boolean;
}

interface InteractionState {
  // Shop/Quest UI
  shopState: ShopState;
  questOfferState: QuestOfferState;
  interactingNPC: NPCData | null;
  // Raw server `talkToNPC` response (real quests + shop). GameUIOverlay (which
  // holds the live player) hydrates this into the rendered NPCInteractionUI.
  // null until the server `npcDialogue` arrives for the open interaction.
  npcDialoguePayload: NpcDialoguePayload | null;
  /** The last turn-in at the open NPC, or null. */
  questCompletion: QuestCompletionNotice | null;
  /**
   * Claim the one-shot contextual quest action (gamepad Options/Start) for
   * `npcId`. Returns true exactly once per press, and only for the NPC the
   * press targeted; always clears, so an unresolvable press cannot leak into
   * the next dialogue.
   */
  consumePendingQuestAction: (npcId: string) => boolean;

  // Chat focus (prevents other hotkeys)
  isChatFocused: boolean;

  // Tutorial state
  tutorialActive: boolean;
}

interface InteractionActions {
  openShop: (npcName: string, items: ShopItem[], npcId?: string, questCount?: number) => void;
  closeShop: () => void;

  /** Toggle the standalone quest window for the currently-interacting NPC.
   *  The npc data is supplied live by GameUIOverlay, so no payload here. */
  openQuestOffer: () => void;
  closeQuestOffer: () => void;

  interactNPC: (npc: NPCData) => void;
  closeNPCInteraction: () => void;

  setChatFocused: (focused: boolean) => void;

  // Tutorial actions
  completeTutorial: () => void;

  /** Close every interaction surface (wired into the Escape "close all" path). */
  closeAllInteractions: () => void;
}

type InteractionContextType = InteractionState & InteractionActions;

const InteractionContext = createContext<InteractionContextType | null>(null);

export const useInteraction = (): InteractionContextType => {
  const context = useContext(InteractionContext);
  if (!context) {
    throw new Error('useInteraction must be used within an InteractionProvider');
  }
  return context;
};

interface InteractionProviderProps {
  children: ReactNode;
}

export const InteractionProvider: React.FC<InteractionProviderProps> = ({ children }) => {
  // Shop state
  const [shopState, setShopState] = useState<ShopState>(CLOSED_SHOP_STATE);

  // Quest offer window toggle (data comes from the live interacting NPC).
  const [questOfferState, setQuestOfferState] = useState<QuestOfferState>({ isOpen: false });

  // NPC Interaction state
  const [interactingNPC, setInteractingNPC] = useState<NPCData | null>(null);
  const [npcDialoguePayload, setNpcDialoguePayload] = useState<NpcDialoguePayload | null>(null);
  const [questCompletion, setQuestCompletion] = useState<Omit<QuestCompletionNotice, 'message'> & { message: string | null } | null>(null);
  // Ref, not state: handleNPCInteract calls closeAllInteractions() while the
  // arming emit is still on the stack, and a batched setState would lose the
  // arm to that clear. Mirrors interactingNPCRef / activeDialogueRequestIdRef,
  // which are assigned after closeAllInteractions for the same reason.
  const pendingQuestActionNpcIdRef = useRef<string | null>(null);
  // Refs mirror the live dialogue state for the []-deps event handlers below
  // (they'd otherwise close over stale state). `hasOptions` gates the E-dismiss:
  // a re-interact only closes a greeting-only panel, never one with quests/shop.
  const interactingNPCRef = useRef<NPCData | null>(null);
  const hasOptionsRef = useRef(false);
  // The NPC id whose VENDOR window is open (for the same walk-out-of-range
  // close the dialogue gets). Dialogue + shop are mutually exclusive, so only
  // one of interactingNPCRef / shopNpcIdRef is set at a time.
  const shopNpcIdRef = useRef<string | null>(null);
  // Each UI-originated talk request gets a provider-scoped monotonic nonce.
  // The active nonce is cleared with the surface; the sequence itself is not,
  // so close/reopen of the same NPC cannot accept a delayed earlier reply.
  const nextDialogueRequestIdRef = useRef(0);
  const activeDialogueRequestIdRef = useRef<number | null>(null);
  // The talk request re-sent after a turn-in (plan 13 §10). Its reply replaces
  // the offers in place; a refusal leaves the window as it was.
  const refreshRequestIdRef = useRef<number | null>(null);

  const clearInteractionAuthority = useCallback(() => {
    interactingNPCRef.current = null;
    hasOptionsRef.current = false;
    shopNpcIdRef.current = null;
    activeDialogueRequestIdRef.current = null;
    refreshRequestIdRef.current = null;
    setActiveDialogueNpcId(null);
  }, []);

  const consumePendingQuestAction = useCallback((npcId: string): boolean => {
    const armed = pendingQuestActionNpcIdRef.current;
    pendingQuestActionNpcIdRef.current = null;
    return armed !== null && armed === npcId;
  }, []);

  const closeAllInteractions = useCallback(() => {
    clearInteractionAuthority();
    setInteractingNPC(null);
    setNpcDialoguePayload(null);
    setQuestCompletion(null);
    pendingQuestActionNpcIdRef.current = null;
    setQuestOfferState({ isOpen: false });
    setShopState(CLOSED_SHOP_STATE);
  }, [clearInteractionAuthority]);

  useEffect(() => {
    const handleNPCInteract = (data: { npcId: string }) => {
      gameLogger.info('ui', `[InteractionContext] NPC interaction event received: ${data.npcId}`);
      // Press-E-again to dismiss: a second interact on the same NPC closes the
      // panel when there are no actionable options (greeting-only).
      if (interactingNPCRef.current?.id === data.npcId && !hasOptionsRef.current) {
        closeAllInteractions();
        return;
      }
      const npc = getNPC(data.npcId);
      if (!npc) return;
      // Open the panel immediately with static data (name/role) for snappy
      // feedback, then ask the server for the authoritative quest/shop state.
      closeAllInteractions();
      const requestId = nextDialogueRequestIdRef.current + 1;
      nextDialogueRequestIdRef.current = requestId;
      activeDialogueRequestIdRef.current = requestId;
      interactingNPCRef.current = npc;
      hasOptionsRef.current = data.npcId === 'guide_rafael'; // static option until payload
      setActiveDialogueNpcId(data.npcId);
      setInteractingNPC(npc);
      EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, {
        action: 'talkToNPC',
        data: { npcId: data.npcId, requestId },
      });
    };

    // Authoritative server reply: real available quests + shop inventory.
    const handleNpcDialogue = (payload: CorrelatedNpcDialoguePayload) => {
      const activeNpcId = interactingNPCRef.current?.id;
      const activeRequestId = activeDialogueRequestIdRef.current;
      if (
        !payload
        || !activeNpcId
        || payload.npcId !== activeNpcId
        || payload.requestId !== activeRequestId
      ) {
        gameLogger.info(
          'ui',
          `[InteractionContext] Ignoring stale npcDialogue for ${payload?.npcId ?? 'unknown'} request ${payload?.requestId ?? 'unknown'}; active NPC is ${activeNpcId ?? 'none'} request ${activeRequestId ?? 'none'}`,
        );
        return;
      }
      const isRefresh = payload.requestId === refreshRequestIdRef.current;
      if (isRefresh) refreshRequestIdRef.current = null;
      if (payload.ok !== true) {
        gameLogger.info('ui', `[InteractionContext] npcDialogue not ok: ${payload.reason}${isRefresh ? ' (turn-in refresh; keeping the window)' : ''}`);
        // The turn-in already succeeded; a refused refresh keeps what is shown.
        if (isRefresh) return;
        closeAllInteractions();
        return;
      }
      // D12: a locked featured teaser counts as quest content, so a teaser-only
      // NPC opens its quest window too (the overlay renders it only when the
      // hydrated NPC really has a teaser).
      const hasQuests = (payload.quests?.length ?? 0) > 0
        || mayHaveFeaturedTeaser(activeNpcId, payload.quests ?? []);
      hasOptionsRef.current = hasQuests
        || (payload.shopItems?.length ?? 0) > 0
        || payload.role === 'trainer';
      setNpcDialoguePayload(payload);
      // If this NPC has quests to offer, open the quest view by default so the
      // player sees them immediately (e.g., talking to Tomas in village_arrival).
      // Mallow Wick uses an authored conversation-first flow where the player chooses
      // the lesson, lore, or leave options directly (R4-Q01).
      if (hasQuests && activeNpcId !== 'mallow_wick' && activeNpcId !== 'npc_mallow_wick') {
        setQuestOfferState({ isOpen: true });
      }
    };

    // The server computes an NPC's offers per talk request, so a quest this
    // turn-in unlocks (First Hunt → A Qualified Soul at Lark) only exists in
    // the NEXT reply. Ask again through the same talkToNPC path the window
    // opens with; the window stays up and its offers update in place, with
    // the completion line on top (plan 13 §10). No protocol change.
    const handleQuestCompleted = (payload: { questId: string; completionMessage?: string; alreadyKnown?: boolean }) => {
      const quest = payload?.questId ? getQuest(payload.questId) : null;
      const npc = interactingNPCRef.current;
      if (!quest || !npc || quest.endNpc !== npc.id) return;

      setQuestCompletion({
        npcId: npc.id,
        questId: payload.questId,
        message: payload.completionMessage ?? null,
        alreadyKnown: payload.alreadyKnown,
      });
      const requestId = nextDialogueRequestIdRef.current + 1;
      nextDialogueRequestIdRef.current = requestId;
      activeDialogueRequestIdRef.current = requestId;
      refreshRequestIdRef.current = requestId;
      EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, {
        action: 'talkToNPC',
        data: { npcId: npc.id, requestId },
      });
    };

    const handleQuestAccepted = () => {
      const npc = interactingNPCRef.current;
      if (!npc) return;

      const requestId = nextDialogueRequestIdRef.current + 1;
      nextDialogueRequestIdRef.current = requestId;
      activeDialogueRequestIdRef.current = requestId;
      refreshRequestIdRef.current = requestId;
      EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, {
        action: 'talkToNPC',
        data: { npcId: npc.id, requestId },
      });
    };

    // Keep an already-open trainer panel on the same quote the server just
    // used. Training depth is shared across ability choices, so one success
    // removes the learned option and advances every remaining option to the
    // returned next quote; a rejection refreshes all choices to its current
    // quote. No ability, price, or wallet state is changed speculatively.
    const handleLearnAbilityResult = (payload: LearnAbilityResultMessage) => {
      const authoritativeQuote = payload.ok === true ? payload.nextQuote : payload.quote;
      setNpcDialoguePayload(previous => {
        if (!previous || previous.role !== 'trainer' || !previous.trainingOptions
          || payload.npcId !== previous.npcId) return previous;
        const remaining = previous.trainingOptions.filter(option => (
          payload.ok !== true || option.abilityId !== payload.abilityId
        ));
        return {
          ...previous,
          trainingOptions: authoritativeQuote
            ? remaining.map(option => ({ ...option, quote: authoritativeQuote }))
            : remaining,
        };
      });
    };

    // Engine says the player walked out of range of the open NPC surface →
    // close whichever is up (dialogue OR vendor window).
    const handleLeftRange = (data: { npcId: string }) => {
      if (
        interactingNPCRef.current?.id === data?.npcId
        || shopNpcIdRef.current === data?.npcId
      ) {
        closeAllInteractions();
      }
    };

    // Gamepad Options/Start: mark this NPC for a one-shot accept-or-turn-in,
    // then run the normal talk path — the server's reply is the only source of
    // authoritative quest state, so the button has to fetch before it can act.
    const handleContextualQuestAction = (data: { npcId: string; npcName: string }) => {
      if (!data?.npcId) return;
      // Emit FIRST: handleNPCInteract runs synchronously here and calls
      // closeAllInteractions(), which clears the arm. Arming afterwards is
      // what makes the press survive the talk it just triggered.
      EventBus.getInstance().emit('npc:interact', {
        npcId: data.npcId,
        npcName: data.npcName,
        npcType: '',
      });
      pendingQuestActionNpcIdRef.current = data.npcId;
    };
    EventBus.getInstance().on(EVENTS.NET_DISCONNECTED, closeAllInteractions);
    EventBus.getInstance().on('quest:contextualAction', handleContextualQuestAction);
    EventBus.getInstance().on('npc:interact', handleNPCInteract);
    EventBus.getInstance().on('net:message:npcDialogue', handleNpcDialogue);
    EventBus.getInstance().on('net:message:questCompleted', handleQuestCompleted);
    EventBus.getInstance().on('net:message:questAccepted', handleQuestAccepted);
    EventBus.getInstance().on(EVENTS.NET_MESSAGE_LEARN_ABILITY_RESULT, handleLearnAbilityResult);
    EventBus.getInstance().on('npc:dialogueLeftRange', handleLeftRange);
    return () => {
      EventBus.getInstance().off(EVENTS.NET_DISCONNECTED, closeAllInteractions);
      EventBus.getInstance().off('quest:contextualAction', handleContextualQuestAction);
      EventBus.getInstance().off('npc:interact', handleNPCInteract);
      EventBus.getInstance().off('net:message:npcDialogue', handleNpcDialogue);
      EventBus.getInstance().off('net:message:questCompleted', handleQuestCompleted);
      EventBus.getInstance().off('net:message:questAccepted', handleQuestAccepted);
      EventBus.getInstance().off(EVENTS.NET_MESSAGE_LEARN_ABILITY_RESULT, handleLearnAbilityResult);
      EventBus.getInstance().off('npc:dialogueLeftRange', handleLeftRange);
      // Interaction state is provider-scoped, but PredictionLoop reads this
      // engine-visible authority directly. Never leave stale focus behind when
      // the provider is replaced during reconnect, navigation, or HMR.
      clearInteractionAuthority();
    };
  }, [clearInteractionAuthority, closeAllInteractions]);

  // Chat focus
  const [isChatFocused, setIsChatFocused] = useState(false);

  // Tutorial state — flipped by TutorialSystem's beat events (Hollow Coil
  // Lane F FTUE): the first shown beat activates the overlay, the COMPLETED
  // step (or the legacy completeTutorial action) deactivates it. Before this
  // wiring the flag was only ever set false, so the FTUE substrate was
  // permanently dormant.
  const [tutorialActive, setTutorialActive] = useState(false);

  useEffect(() => {
    const bus = EventBus.getInstance();
    const handleStepChanged = (data: { step: string }) => {
      setTutorialActive(data?.step !== 'completed');
    };
    const unsub = bus.on(EVENTS.TUTORIAL_STEP_CHANGED, handleStepChanged);
    return unsub;
  }, []);

  // Shop actions
  const openShop = useCallback((npcName: string, items: ShopItem[], npcId: string = '', questCount?: number) => {
    // Mutually exclusive with the dialogue panel — only one NPC surface at a
    // time. Opening the shop closes the dialogue, and keeps the engine-visible
    // active-NPC id pointed at THIS npc so the vendor window also auto-closes
    // when the player walks out of range (same as the dialogue).
    closeAllInteractions();
    shopNpcIdRef.current = npcId;
    setActiveDialogueNpcId(npcId);
    setShopState({ isOpen: true, npcId, npcName, items, questCount });
  }, [closeAllInteractions]);

  const closeShop = useCallback(() => {
    closeAllInteractions();
  }, [closeAllInteractions]);

  // Quest offer actions
  const openQuestOffer = useCallback(() => {
    setQuestOfferState({ isOpen: true });
  }, []);

  const closeQuestOffer = useCallback(() => {
    setQuestOfferState({ isOpen: false });
  }, []);

  // NPC Interaction actions
  const interactNPC = useCallback((npc: NPCData) => {
    closeAllInteractions();
    interactingNPCRef.current = npc;
    hasOptionsRef.current = npc.availableQuests.length > 0
      || (npc.shopItems?.length ?? 0) > 0
      || npc.role === 'trainer';
    setActiveDialogueNpcId(npc.id);
    setInteractingNPC(npc);
  }, [closeAllInteractions]);

  const closeNPCInteraction = useCallback(() => {
    closeAllInteractions();
  }, [closeAllInteractions]);

  // Chat focus
  const setChatFocused = useCallback((focused: boolean) => {
    setIsChatFocused(focused);
  }, []);

  // Tutorial actions
  const completeTutorial = useCallback(() => {
    setTutorialActive(false);
    gameLogger.info('ui', '🎓 Tutorial completed! Full HUD revealed.');
  }, []);

  const value: InteractionContextType = {
    shopState,
    questOfferState,
    interactingNPC,
    npcDialoguePayload,
    questCompletion: questCompletion && {
      ...questCompletion,
      message: questCompletion.message ?? npcDialoguePayload?.dialogue?.complete ?? 'Thank you. It is done.',
    },
    consumePendingQuestAction,
    isChatFocused,
    tutorialActive,
    openShop,
    closeShop,
    openQuestOffer,
    closeQuestOffer,
    interactNPC,
    closeNPCInteraction,
    setChatFocused,
    completeTutorial,
    closeAllInteractions,
  };

  return <InteractionContext.Provider value={value}>{children}</InteractionContext.Provider>;
};
